import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../services/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// Create the context
const SubscriptionContext = createContext({
  isPremium: false,
  subscriptionStatus: null,
  subscriptionTier: null,
  currentPeriodEnd: null,
  isTrialing: false,
  trialEnd: null,
  cancelAtPeriodEnd: false,
  loading: true,
  error: null
});

/**
 * SubscriptionProvider
 * Provides subscription state to the entire app via Firestore real-time listener
 */
export function SubscriptionProvider({ children }) {
  const { currentUser } = useAuth();
  
  const [subscriptionState, setSubscriptionState] = useState({
    isPremium: false,
    subscriptionStatus: null,
    subscriptionTier: null,
    currentPeriodEnd: null,
    isTrialing: false,
    trialEnd: null,
    cancelAtPeriodEnd: false,
    loading: true,
    error: null
  });

  useEffect(() => {
    // Reset state when user logs out
    if (!currentUser) {
      setSubscriptionState({
        isPremium: false,
        subscriptionStatus: null,
        subscriptionTier: null,
        currentPeriodEnd: null,
        isTrialing: false,
        trialEnd: null,
        cancelAtPeriodEnd: false,
        loading: false,
        error: null
      });
      return;
    }

    // Set up real-time listener on user document
    const userDocRef = doc(db, 'users', currentUser.uid);
    
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          
          // Parse dates from Firestore Timestamps
          const currentPeriodEnd = userData.currentPeriodEnd?.toDate?.() 
            ? userData.currentPeriodEnd.toDate() 
            : userData.currentPeriodEnd 
              ? new Date(userData.currentPeriodEnd) 
              : null;
          
          const trialEnd = userData.trialEnd?.toDate?.() 
            ? userData.trialEnd.toDate() 
            : userData.trialEnd 
              ? new Date(userData.trialEnd) 
              : null;

          // Determine premium status
          // User is premium if:
          // 1. isPremium is true AND
          // 2. (status is active/trialing OR (status is canceled but period hasn't ended))
          const now = new Date();
          let isPremium = userData.isPremium || false;
          
          if (userData.subscriptionStatus === 'canceled' && currentPeriodEnd) {
            // Grace period: keep premium until period ends
            isPremium = currentPeriodEnd > now;
          } else if (userData.subscriptionStatus === 'past_due') {
            // Still allow access during past_due (Stripe will retry payment)
            isPremium = userData.isPremium || false;
          }

          setSubscriptionState({
            isPremium,
            subscriptionStatus: userData.subscriptionStatus || null,
            subscriptionTier: userData.subscriptionTier || null,
            currentPeriodEnd,
            isTrialing: userData.isTrialing || false,
            trialEnd,
            cancelAtPeriodEnd: userData.cancelAtPeriodEnd || false,
            loading: false,
            error: null
          });
        } else {
          // User document doesn't exist
          setSubscriptionState({
            isPremium: false,
            subscriptionStatus: null,
            subscriptionTier: null,
            currentPeriodEnd: null,
            isTrialing: false,
            trialEnd: null,
            cancelAtPeriodEnd: false,
            loading: false,
            error: null
          });
        }
      },
      (error) => {
        console.error('Error listening to subscription data:', error);
        setSubscriptionState(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [currentUser]);

  return (
    <SubscriptionContext.Provider value={subscriptionState}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * useSubscription hook
 * Access subscription state from any component
 * 
 * @returns {Object} Subscription state
 * @property {boolean} isPremium - Whether user has active premium access
 * @property {string|null} subscriptionStatus - Current status (active, trialing, canceled, past_due)
 * @property {string|null} subscriptionTier - Subscription tier (premium)
 * @property {Date|null} currentPeriodEnd - When current billing period ends
 * @property {boolean} isTrialing - Whether user is on trial
 * @property {Date|null} trialEnd - When trial ends
 * @property {boolean} cancelAtPeriodEnd - Whether subscription will cancel at period end
 * @property {boolean} loading - Whether subscription data is loading
 * @property {string|null} error - Error message if any
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  
  return context;
}

export default SubscriptionContext;
